import { config } from '../config/environment';

export interface DocumentFolder {
  id: string;
  name: string;
  client_id: string | null;
  created_at: string | null;
}

export interface DocumentItem {
  id: string;
  coach_id: string;
  name: string;
  file_name: string;
  file_type: string;
  file_size: number;
  is_template: boolean;
  document_type: string | null;
  document_type_id: string | null;
  folder_id: string | null;
  uploader_client_id: string | null;
  consent_items?: ConsentItem[] | null;
  created_at: string | null;
}

export interface DocumentType {
  id: string;
  name: string;
  created_at: string | null;
}

export interface ViewUrlResponse {
  url: string;
  file_type: string;
  doc_name: string;
}

export interface ClientFolder {
  id: string;
  name: string;
  parent_folder_id: string | null;
  created_at: string | null;
}

export interface OnboardingClient {
  coach_client_id: number;
  client_id: string;
  client_name: string;
  client_email: string;
  onboarding_step: 'invited' | 'registered' | 'packet_sent' | 'active';
  created_at: string | null;
  /** 'none' = no packet sent, 'pending' = awaiting signature, 'signed' = signed in-app, 'signed_offline' = sent and signed outside CoachSight */
  packet_status: 'none' | 'pending' | 'signed' | 'signed_offline';
  /** null = not yet answered, true = consented, false = declined */
  ai_consent: boolean | null;
}

export interface ClientFolderDocItem {
  document_id: string;
  document_name: string;
  file_type: string;
  view_url: string | null;
}

export interface ClientFolderPacket {
  id: string;
  title: string;
  status: 'pending' | 'signed' | 'signed_offline';
  sent_at: string | null;
  signed_at: string | null;
  sign_token: string;
  documents: ClientFolderDocItem[];
}

export interface ConsentItem {
  label: string;
  required: boolean;
}

export interface PacketDocItem {
  id: string;
  document_id: string;
  sort_order: number;
  filled_file_path: string | null;
  pdf_file_path: string | null;
  document: DocumentItem | null;
  view_url?: string | null;
  signing_pdf_url?: string | null;
}

export interface SignatureRecord {
  id: string;
  client_id: string;
  ip_address: string | null;
  consents: Record<string, boolean> | null;
  signed_at: string | null;
}

export interface DocumentPacket {
  id: string;
  coach_client_id: string;
  title: string;
  status: 'pending' | 'signed' | 'signed_offline';
  sign_token: string;
  sign_token_expires_at: string;
  sent_at: string | null;
  signed_at: string | null;
  consent_items?: ConsentItem[] | null;
  items: PacketDocItem[];
  signatures: SignatureRecord[];
}

export interface DocumentPacketSummary {
  id: string;
  coach_client_id: string;
  title: string;
  status: 'pending' | 'signed' | 'signed_offline';
  sign_token: string;
  sign_token_expires_at: string;
  sent_at: string | null;
  signed_at: string | null;
  signed_ip_address?: string | null;
  folder_id?: string | null;
  client_name?: string;
  client_email?: string;
  coach_id?: string | null;
  coach_first_name?: string | null;
  coach_last_name?: string | null;
}

const BASE = config.apiBaseUrl;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken') || '';
  return { Authorization: `Bearer ${token}` };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ─── Coach API ───────────────────────────────────────────────────────────────

export async function uploadDocument(
  name: string,
  file: File,
  isTemplate = false,
  documentType?: string,
  folderId?: string,
  documentTypeId?: string,
  consentItems?: ConsentItem[],
): Promise<DocumentItem> {
  const form = new FormData();
  form.append('name', name);
  form.append('file', file);
  if (isTemplate) form.append('is_template', 'true');
  if (documentType) form.append('document_type', documentType);
  if (documentTypeId) form.append('document_type_id', documentTypeId);
  if (folderId) form.append('folder_id', folderId);
  if (consentItems && consentItems.length > 0) {
    form.append('consent_items', JSON.stringify(consentItems));
  }
  const res = await fetch(`${BASE}/documents/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  return handleResponse<DocumentItem>(res);
}

export async function parseTemplateTags(file: File): Promise<string[]> {
  const JSZip = (await import('jszip')).default;
  const buf = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buf);
  const xmlFile = zip.file('word/document.xml');
  if (!xmlFile) return [];
  const xml = await xmlFile.async('text');
  // Word splits text across multiple <w:t> runs, so join all text nodes before searching.
  const textNodes = xml.match(/<w:t[^>]*>([^<]*)<\/w:t>/g) ?? [];
  const joined = textNodes.map(n => n.replace(/<[^>]+>/g, '')).join('');
  const matches = joined.match(/\{\{(\w+)\}\}/g) ?? [];
  return Array.from(new Set(matches.map(m => m.slice(2, -2)))).sort();
}

export async function listDocuments(): Promise<DocumentItem[]> {
  const res = await fetch(`${BASE}/documents`, { headers: authHeaders() });
  return handleResponse<DocumentItem[]>(res);
}

export async function listFolders(): Promise<DocumentFolder[]> {
  const res = await fetch(`${BASE}/documents/folders`, { headers: authHeaders() });
  return handleResponse<DocumentFolder[]>(res);
}

export async function createFolder(name: string, clientId?: string): Promise<DocumentFolder> {
  const res = await fetch(`${BASE}/documents/folders`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, ...(clientId ? { client_id: clientId } : {}) }),
  });
  return handleResponse<DocumentFolder>(res);
}

export async function moveDocument(documentId: string, folderId: string | null): Promise<DocumentItem> {
  const res = await fetch(`${BASE}/documents/${documentId}/folder`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder_id: folderId }),
  });
  return handleResponse<DocumentItem>(res);
}

export async function movePacketToFolder(packetId: string, folderId: string | null): Promise<void> {
  const res = await fetch(`${BASE}/documents/packets/${packetId}/folder`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder_id: folderId }),
  });
  return handleResponse<void>(res);
}

export async function deleteDocument(id: string): Promise<void> {
  const res = await fetch(`${BASE}/documents/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handleResponse<void>(res);
}

export async function createPacket(
  coachClientId: string,
  title: string,
  documentIds: string[],
  consentItems?: ConsentItem[],
  tagValues?: Record<string, string>,
): Promise<DocumentPacket> {
  const res = await fetch(`${BASE}/documents/packets`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      coach_client_id: coachClientId,
      title,
      document_ids: documentIds,
      ...(consentItems && consentItems.length > 0 ? { consent_items: consentItems } : {}),
      ...(tagValues && Object.keys(tagValues).length > 0 ? { tag_values: tagValues } : {}),
    }),
  });
  return handleResponse<DocumentPacket>(res);
}

export async function deletePacket(packetId: string): Promise<void> {
  const res = await fetch(`${BASE}/documents/packets/${packetId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handleResponse<void>(res);
}

export async function listPacketsCoach(): Promise<DocumentPacketSummary[]> {
  const res = await fetch(`${BASE}/documents/packets`, { headers: authHeaders() });
  return handleResponse<DocumentPacketSummary[]>(res);
}

export async function getPacketCoach(packetId: string): Promise<DocumentPacket> {
  const res = await fetch(`${BASE}/documents/packets/${packetId}`, { headers: authHeaders() });
  return handleResponse<DocumentPacket>(res);
}

// ─── Client API ──────────────────────────────────────────────────────────────

export async function listPacketsClient(): Promise<DocumentPacketSummary[]> {
  const res = await fetch(`${BASE}/documents/packets/for-client`, { headers: authHeaders() });
  return handleResponse<DocumentPacketSummary[]>(res);
}

export async function getPacketByToken(token: string, signingToken?: string): Promise<DocumentPacket> {
  const auth = signingToken ?? (localStorage.getItem('authToken') || '');
  const res = await fetch(`${BASE}/documents/packets/by-token/${token}`, {
    headers: { Authorization: `Bearer ${auth}` },
  });
  return handleResponse<DocumentPacket>(res);
}

export async function signPacket(
  token: string,
  consents?: Record<string, boolean>,
  signingToken?: string,
): Promise<{ message: string; signed_at: string }> {
  // Use || (not ??) so that an empty string signingToken (logged-in client skipping OTP) falls through to authToken
  const auth = signingToken || (localStorage.getItem('authToken') || '');
  const res = await fetch(`${BASE}/documents/packets/by-token/${token}/sign`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ consents: consents ?? null }),
  });
  return handleResponse(res);
}

export async function requestSigningOtp(token: string, email: string): Promise<{ message: string }> {
  const res = await fetch(`${BASE}/documents/packets/by-token/${token}/request-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return handleResponse(res);
}

export async function verifySigningOtp(
  token: string,
  email: string,
  code: string,
): Promise<{ signing_token: string }> {
  const res = await fetch(`${BASE}/documents/packets/by-token/${token}/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  return handleResponse(res);
}

// ─── File URL helper (token-gated, no JWT) ───────────────────────────────────

export function documentViewUrl(token: string, documentId: string): string {
  return `${BASE}/documents/view/${token}/${documentId}`;
}

export function documentSigningPdfUrl(token: string, documentId: string): string {
  return `${BASE}/documents/packets/by-token/${token}/pdf/${documentId}`;
}

// ─── Onboarding pipeline ─────────────────────────────────────────────────────

export async function getOnboardingPipeline(): Promise<OnboardingClient[]> {
  const res = await fetch(`${BASE}/coaches/me/onboarding-pipeline`, { headers: authHeaders() });
  return handleResponse<OnboardingClient[]>(res);
}

// ─── Client folder (coach-side, by relationship) ─────────────────────────────

export async function listClientFiles(coachClientId: number): Promise<ClientFolderPacket[]> {
  const res = await fetch(`${BASE}/documents/clients/${coachClientId}/files`, { headers: authHeaders() });
  return handleResponse<ClientFolderPacket[]>(res);
}

// ─── Document types ───────────────────────────────────────────────────────────

export async function listDocumentTypes(): Promise<DocumentType[]> {
  const res = await fetch(`${BASE}/documents/types`, { headers: authHeaders() });
  return handleResponse<DocumentType[]>(res);
}

export async function createDocumentType(name: string): Promise<DocumentType> {
  const res = await fetch(`${BASE}/documents/types`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  return handleResponse<DocumentType>(res);
}

export async function deleteDocumentType(id: string): Promise<void> {
  const res = await fetch(`${BASE}/documents/types/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handleResponse<void>(res);
}

// ─── Copy document ────────────────────────────────────────────────────────────

export async function copyDocument(docId: string, targetFolderId?: string): Promise<DocumentItem> {
  const res = await fetch(`${BASE}/documents/${docId}/copy`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_folder_id: targetFolderId ?? null }),
  });
  return handleResponse<DocumentItem>(res);
}

// ─── View URL (JWT-authenticated presigned URL) ───────────────────────────────

export async function getDocumentViewUrl(docId: string): Promise<ViewUrlResponse> {
  const res = await fetch(`${BASE}/documents/${docId}/view-url`, { headers: authHeaders() });
  return handleResponse<ViewUrlResponse>(res);
}

// ─── Client self-serve files ─────────────────────────────────────────────────

export async function listClientOwnFolders(): Promise<ClientFolder[]> {
  const res = await fetch(`${BASE}/documents/client-folders`, { headers: authHeaders() });
  return handleResponse<ClientFolder[]>(res);
}

export async function createClientFolderSvc(name: string, parentFolderId?: string): Promise<ClientFolder> {
  const res = await fetch(`${BASE}/documents/client-folders`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, parent_folder_id: parentFolderId ?? null }),
  });
  return handleResponse<ClientFolder>(res);
}

export async function clientUploadDocument(name: string, file: File, folderId?: string): Promise<DocumentItem> {
  const form = new FormData();
  form.append('name', name);
  form.append('file', file);
  if (folderId) form.append('folder_id', folderId);
  const res = await fetch(`${BASE}/documents/client-upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  return handleResponse<DocumentItem>(res);
}

export async function listClientOwnFiles(): Promise<DocumentItem[]> {
  const res = await fetch(`${BASE}/documents/client-files`, { headers: authHeaders() });
  return handleResponse<DocumentItem[]>(res);
}

export async function deleteClientFile(id: string): Promise<void> {
  const res = await fetch(`${BASE}/documents/client-files/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handleResponse<void>(res);
}

// ─── Template tags ────────────────────────────────────────────────────────────

export interface DocumentTemplateTag {
  id: string;
  coach_id: string;
  key: string;
  label: string;
  default_value: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface SystemTag {
  key: string;
  label: string;
  description: string;
}

export async function listTemplateTags(): Promise<DocumentTemplateTag[]> {
  const res = await fetch(`${BASE}/documents/tags`, { headers: authHeaders() });
  return handleResponse<DocumentTemplateTag[]>(res);
}

export async function listSystemTags(): Promise<SystemTag[]> {
  const res = await fetch(`${BASE}/documents/tags/system`, { headers: authHeaders() });
  return handleResponse<SystemTag[]>(res);
}

export async function createTemplateTag(
  key: string,
  label: string,
  defaultValue: string,
): Promise<DocumentTemplateTag> {
  const res = await fetch(`${BASE}/documents/tags`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, label, default_value: defaultValue }),
  });
  return handleResponse<DocumentTemplateTag>(res);
}

export async function updateTemplateTag(
  id: string,
  updates: { label?: string; default_value?: string },
): Promise<DocumentTemplateTag> {
  const res = await fetch(`${BASE}/documents/tags/${id}`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  return handleResponse<DocumentTemplateTag>(res);
}

export async function deleteTemplateTag(id: string): Promise<void> {
  const res = await fetch(`${BASE}/documents/tags/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handleResponse<void>(res);
}
